// Kulki (.ai/game-rules.md "Kulki", owner 2026-11-03): coloured balls on a 9 x 9 board. A ball moves to an empty cell
// it can reach through empty cells (up, down, left, right); 5 or more of one colour in a row (across, down, diagonal)
// leave the board and score; a move that makes no line brings the 3 balls shown as next. The game ends when the board is
// full. The random sequence of the new balls is part of the state (seeded from the deal's pattern), so a move undone and
// made again brings the same balls, and Retry plays the same game. Pure: no timer, no screen.

export const SIZE = 9;
export const CELLS = SIZE * SIZE;
export const COLOURS = 7;
/** Balls that come after a move without a line, and the balls of a new game. */
export const NEXT = 3;
export const START = 5;
/** The shortest line that leaves the board: 5, or 4 in the easier version (owner 2026-11-03: "dwie wersje ... pięć
 *  kulek ... a druga, łatwiejsza wersja, cztery kulki"; picked in the Games window, 5 by default). */
export const LINES = [5, 4] as const;
export const LINE = 5;

/** A cell: 0 empty, 1..COLOURS a ball of that colour. */
export type Cell = number;

export interface KulkiState {
  /** CELLS cells, row by row from the top left. */
  readonly cells: readonly Cell[];
  /** The colours of the next NEXT balls (shown above the board). */
  readonly next: readonly number[];
  readonly score: number;
  /** Moves made (a ball moved). */
  readonly moves: number;
  /** Lines that left the board, and the longest of them (the end window). */
  readonly lines: number;
  readonly longest: number;
  /** The random sequence's state (mulberry32). */
  readonly seed: number;
  readonly over: boolean;
  /** The shortest line that leaves (LINES). */
  readonly line: number;
  /** The deal's pattern (Retry, a report). */
  readonly pattern: readonly number[];
}

/** What a move did, for the view's animation. */
export interface MoveResult {
  readonly state: KulkiState;
  /** The cells the ball went through, from the start to the end. */
  readonly path: readonly number[];
  /** The cells that left the board after the move (the moved ball's lines). */
  readonly cleared: readonly number[];
  /** The cells where new balls landed (none after a line). */
  readonly added: readonly number[];
  /** The cells that left after the new balls landed (their own lines). */
  readonly clearedAfter: readonly number[];
  readonly points: number;
}

/** mulberry32: the next number in [0, 1) and the next state. */
function random(seed: number): [number, number] {
  const s = (seed + 0x6d2b79f5) | 0;
  let t = s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return [((t ^ (t >>> 14)) >>> 0) / 4294967296, s];
}

/** The seed of a deal's pattern (FNV-1a over its numbers). */
export function seedOf(pattern: readonly number[]): number {
  let h = 0x811c9dc5;
  for (const n of pattern) {
    h ^= n & 0xff;
    h = Math.imul(h, 0x01000193);
    h ^= (n >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}

const row = (i: number) => Math.floor(i / SIZE);
const col = (i: number) => i % SIZE;

/** Points for one line of `n` balls, when `line` is the shortest that leaves: n x (n - line + 1) x 2 (5 in a row: 5 = 10,
 *  6 = 24, 7 = 42; 4 in a row: 4 = 8, 5 = 20, 6 = 36). */
export const linePoints = (n: number, line: number = LINE): number => (n >= line ? n * (n - line + 1) * 2 : 0);

/** Draws `n` colours. */
function drawColours(seed: number, n: number): [number[], number] {
  const out: number[] = [];
  let s = seed;
  for (let k = 0; k < n; k++) {
    const [r, s2] = random(s);
    s = s2;
    out.push(1 + Math.floor(r * COLOURS));
  }
  return [out, s];
}

/** Puts `colours` on random empty cells (as many as fit). */
/** A step through the board that visits every cell (37 and 81 have no common factor). */
const PROBE = 37;

/** Puts `colours` on the board (as many as fit). Each ball's cell comes from the deal, not from the board (owner
 *  2026-11-03, online: "każdy gracz musi dostać te same ułożenie"): one number a ball names a cell; a taken cell sends the
 *  ball on in steps of PROBE to the first empty one. So two players of the same deal get every new ball on the same cell
 *  whenever that cell is empty on both boards, and the sequence stays in step (one number a ball, always). */
function place(cells: Cell[], colours: readonly number[], seed: number): [number[], number] {
  const added: number[] = [];
  let s = seed;
  for (const c of colours) {
    const [r, s2] = random(s);
    s = s2;
    let at = Math.floor(r * CELLS);
    for (let k = 0; k < CELLS && cells[at]; k++) at = (at + PROBE) % CELLS;
    if (cells[at]) continue; // a full board: the game is over
    cells[at] = c;
    added.push(at);
  }
  return [added, s];
}

/** The lines of `line` or more through `at` (its colour), each a list of cells. */
export function linesThrough(cells: readonly Cell[], at: number, line: number = LINE): number[][] {
  const c = cells[at];
  if (!c) return [];
  const out: number[][] = [];
  for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]] as const) {
    const run = [at];
    for (const sign of [1, -1]) {
      let r = row(at) + dr * sign, k = col(at) + dc * sign;
      while (r >= 0 && r < SIZE && k >= 0 && k < SIZE && cells[r * SIZE + k] === c) {
        run.push(r * SIZE + k);
        r += dr * sign;
        k += dc * sign;
      }
    }
    if (run.length >= line) out.push(run);
  }
  return out;
}

/** Takes the lines through `from` cells off the board: the cells cleared and the points (each line on its own). */
function clearLines(cells: Cell[], from: readonly number[], len: number): { cleared: number[]; points: number; lines: number; longest: number } {
  const lines = from.flatMap((at) => linesThrough(cells, at, len));
  const seen = new Set<string>();
  let points = 0, longest = 0;
  const cleared = new Set<number>();
  for (const line of lines) {
    const key = [...line].sort((a, b) => a - b).join(',');
    if (seen.has(key)) continue; // two new balls in the same line
    seen.add(key);
    points += linePoints(line.length, len);
    longest = Math.max(longest, line.length);
    for (const i of line) cleared.add(i);
  }
  for (const i of cleared) cells[i] = 0;
  return { cleared: [...cleared].sort((a, b) => a - b), points, lines: seen.size, longest };
}

/** A new game: START balls on the board, the next NEXT drawn; `line` the shortest line that leaves. */
export function newGame(pattern: readonly number[], line: number = LINE): KulkiState {
  const cells: Cell[] = Array<Cell>(CELLS).fill(0);
  let seed = seedOf(pattern);
  const [first, s1] = drawColours(seed, START);
  const [, s2] = place(cells, first, s1);
  const [next, s3] = drawColours(s2, NEXT);
  seed = s3;
  // a start with a line would score for nothing: take it off without points (it cannot happen with 5 balls of 7 colours
  // unless all 5 are one colour in a row; kept for safety)
  clearLines(cells, cells.flatMap((v, i) => (v ? [i] : [])), line);
  return { cells, next, score: 0, moves: 0, lines: 0, longest: 0, seed, over: false, line, pattern: [...pattern] };
}

/** The shortest path of empty cells from `from` to `to` (both included), or null. */
export function pathTo(cells: readonly Cell[], from: number, to: number): number[] | null {
  if (from === to || !cells[from] || cells[to]) return null;
  const prev = new Int16Array(CELLS).fill(-1);
  prev[from] = from;
  const queue = [from];
  for (let q = 0; q < queue.length; q++) {
    const at = queue[q]!;
    if (at === to) break;
    const r = row(at), k = col(at);
    for (const n of [r > 0 ? at - SIZE : -1, r < SIZE - 1 ? at + SIZE : -1, k > 0 ? at - 1 : -1, k < SIZE - 1 ? at + 1 : -1]) {
      if (n < 0 || prev[n] !== -1 || cells[n]) continue;
      prev[n] = at;
      queue.push(n);
    }
  }
  if (prev[to] === -1) return null;
  const path = [to];
  while (path[0] !== from) path.unshift(prev[path[0]!]!);
  return path;
}

/** Every empty cell the ball on `from` can reach. */
export function reachable(cells: readonly Cell[], from: number): Set<number> {
  const out = new Set<number>();
  if (!cells[from]) return out;
  const queue = [from];
  const seen = new Uint8Array(CELLS);
  seen[from] = 1;
  for (let q = 0; q < queue.length; q++) {
    const at = queue[q]!;
    const r = row(at), k = col(at);
    for (const n of [r > 0 ? at - SIZE : -1, r < SIZE - 1 ? at + SIZE : -1, k > 0 ? at - 1 : -1, k < SIZE - 1 ? at + 1 : -1]) {
      if (n < 0 || seen[n] || cells[n]) continue;
      seen[n] = 1;
      out.add(n);
      queue.push(n);
    }
  }
  return out;
}

/** Moves the ball on `from` to `to`; null when it cannot go there. */
export function move(s: KulkiState, from: number, to: number): MoveResult | null {
  if (s.over) return null;
  const path = pathTo(s.cells, from, to);
  if (!path) return null;
  const cells = [...s.cells];
  cells[to] = cells[from]!;
  cells[from] = 0;
  const first = clearLines(cells, [to], s.line);
  let seed = s.seed, next = s.next, added: number[] = [], after = { cleared: [] as number[], points: 0, lines: 0, longest: 0 };
  if (!first.cleared.length) {
    [added, seed] = place(cells, s.next, seed);
    after = clearLines(cells, added, s.line);
    [next, seed] = drawColours(seed, NEXT);
  }
  const points = first.points + after.points;
  const over = cells.every((v) => v !== 0);
  return {
    state: {
      ...s, cells, next, seed, score: s.score + points, moves: s.moves + 1, over,
      lines: s.lines + first.lines + after.lines, longest: Math.max(s.longest, first.longest, after.longest),
    },
    path,
    cleared: first.cleared,
    added,
    clearedAfter: after.cleared,
    points,
  };
}

/** The board as text for an error report: one row a line, `.` empty, 1-7 a ball; then the next balls and the score. */
export function boardText(s: KulkiState): string {
  const rows: string[] = [];
  for (let r = 0; r < SIZE; r++) rows.push(s.cells.slice(r * SIZE, r * SIZE + SIZE).map((v) => (v ? String(v) : '.')).join(''));
  return `${rows.join('\n')}\nnext ${s.next.join(' ')}, score ${s.score}, moves ${s.moves}, line ${s.line}${s.over ? ', over' : ''}`;
}
